import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { CustomerI, Page } from 'app/elements-v2/models/page';
import { DiscoverV2BffService } from 'app/elements-v2/service/discover-v2-bff.service';
import { combineLatest, Subscription } from 'rxjs';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { Configuration } from 'shared/models/configuration';
import { StaffUser, User, UserPermission } from 'user/models/user';
import { getUserState, UserState, doesUserHavePermission } from 'user/reducers/user.reducer';
import { ForLaterListDto } from 'app/list/models/list.dto';
import { ListItem, ListType, ListWithItemsCount } from 'app/list/models/list';
import { ItemDetail } from '../../models/custom-events';
import { ElementsV2Service } from '../../service/elements-v2.service';
import { SiteTheme } from '../../models/settings';
import { getLists } from 'app/list/reducers/list.reducer';
import { CheckoutsState } from 'user/models/checkouts';
import { HoldsState } from 'user/models/holds';
import { FeatureToggleService } from 'app/services/feature-toggle.service';
import { SitePermissions } from 'app/permissions/models/permissions';
import {
  isAuthorizedInKeycloak,
  isStaffAuthorizedInKeycloak,
} from 'app/keycloak/reducers/keycloak.reducer';
import { CustomerFeature } from 'app/customer-integration/customer-integration';
import { CustomerFeatureToggleService } from 'app/customer-integration/services/customer-feature-toggle.service';
import { PermissionService } from 'app/permissions/services/permission.service';
import { ListSearchService } from 'app/list/services/list-search.service';
import {
  ShareOnSocialMediaService,
  SocialMediaType
} from '../../../services/share-on-social-media.service';
import {WindowRefService} from 'app/services/window-ref.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CitationExportContentComponent } from 'app/citation/components/citation-modal-window/citation-modal-window.component';
import { EmailPopupComponent } from 'app/share-it/components/email-popup/email-popup.component';
@Component({
  selector: 'app-account-portal-v2',
  templateUrl: './account-portal-v2.component.html',
  styleUrls: ['./account-portal-v2.component.scss'],
})
export class AccountPortalV2Component implements OnInit, OnDestroy {
  @Input() translations: any = {};
  private readonly subscriptions = new Subscription();
  public customer: CustomerI;
  public config: Configuration;
  public flags?: string[];
  public user: User;
  public staffUser: StaffUser;
  public forLaterList: ForLaterListDto;
  public bookmarksList: ListItem[];
  public checkoutsCount: number;
  public holdsCount: number;
  public portalFeaturedShowcaseRef: string;
  public portalFallbackShowcaseRef: string;
  public portalFeaturedShowcaseTitle: string;
  public sitePermissions = SitePermissions;
  public loading: boolean;
  public showcasesEnabled: boolean;
  public isAuthPatronFlagEnabled: boolean;
  public accountLinkingEnabled: boolean;

  constructor(
    private readonly router: Router,
    private readonly windowRefService: WindowRefService,
    private readonly shareOnSocialMediaService: ShareOnSocialMediaService,
    private readonly configLoader: ConfigurationLoader,
    private readonly discoverV2BffService: DiscoverV2BffService,
    private readonly elementsV2Service: ElementsV2Service,
    private readonly cdr: ChangeDetectorRef,
    private readonly store: Store<UserState>,
    private readonly listSearchService: ListSearchService,
    private readonly featureToggleService: FeatureToggleService,
    private readonly customerFeatureService: CustomerFeatureToggleService,
    private readonly permissionService: PermissionService,
    private readonly modal: NgbModal
  ) {
    this.customer = this.discoverV2BffService.customer;
    this.config = this.configLoader.config;
    this.loading = true;
  }

  private async setupTheme() {
    await this.elementsV2Service.loadStyle(SiteTheme.VEGA_CLASSIC_V2);
  }

  ngOnInit(): void {
    this.isAuthPatronFlagEnabled =
      this.featureToggleService.getToggles()['DIS-30793_2024-04-27_auth_patron'];

    this.flags = Object.entries(this.featureToggleService.getToggles())
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
    this.subscriptions.add(
      combineLatest([
        this.discoverV2BffService.$page,
        this.store.select(getUserState),
        this.store.select(getLists),
        this.store.pipe(select(isAuthorizedInKeycloak)),
        this.store.pipe(select(isStaffAuthorizedInKeycloak)),
        this.store.pipe(
          select(doesUserHavePermission(UserPermission.SHOWCASES_VIEW))
        ),
      ]).subscribe(
        async ([
          page,
          userState,
          lists,
          isPatronAuthorized,
          isStaffAuthorized,
          hasViewShowcasesPermission,
        ]) => {
          this.setUserState(page, userState, lists);
          this.setShowcasesEnabled(
            isPatronAuthorized,
            isStaffAuthorized,
            hasViewShowcasesPermission
          );
          await this.setupTheme();
          this.cdr.detectChanges();
        }
      )
    );
  }

  public setShowcasesEnabled(
    isPatronAuthorized: boolean,
    isStaffAuthorized: boolean,
    hasViewShowcasesPermission: boolean
  ): void {
    const toggles = this.featureToggleService.getToggles();
    const isAuthorized = isPatronAuthorized || isStaffAuthorized;
    const currentSiteResource = this.configLoader.siteId
      ? `site-${this.configLoader.siteId}`
      : 'main-site';

    if (
      !toggles.licenseCustomShowcases ||
      !isAuthorized ||
      !hasViewShowcasesPermission
    ) {
      this.showcasesEnabled = false;
      return;
    }

    if (this.isAuthPatronFlagEnabled) {
      const hasPermission = this.permissionService.hasPermission(
        currentSiteResource,
        SitePermissions.SHOWCASE_VIEW
      );
      this.showcasesEnabled =
        hasPermission &&
        this.isFeatureAvailable(CustomerFeature.BookshelfShowcasesTab);
      return;
    }

    const hasUserPermission = this.user?.permissions?.includes(
      UserPermission.SHOWCASES_VIEW
    );
    this.showcasesEnabled =
      hasUserPermission &&
      this.isFeatureAvailable(CustomerFeature.BookshelfShowcasesTab);
  }

  private isFeatureAvailable(customerFeature: CustomerFeature): boolean {
    return this.customerFeatureService.isFeatureAvailable(customerFeature);
  }

  public setUserState(
    page: Page,
    userState: UserState,
    lists: ListWithItemsCount[]
  ) {
    const currentUser = userState.currentUser;
    const currentStaffUser = userState.currentStaffUser;
    const checkouts = userState.checkoutsState.toJS() as CheckoutsState;
    const holds = userState.holdsState.toJS() as HoldsState;

    if (!this.isDataReady(currentUser, currentStaffUser, checkouts, holds, page)) {
      return;
    }

    this.updateUserAndStaffInfo(currentUser, currentStaffUser);
    this.updateCheckoutAndHoldCounts(checkouts, holds);
    this.updateShowcaseSettings(page);
    this.updateLists(lists);
    this.loading = false;
    this.accountLinkingEnabled =
      currentUser?.profileConfiguration?.accountLinkingEnabled;
  }

  private isDataReady(
    currentUser: User,
    currentStaffUser: StaffUser,
    checkouts: CheckoutsState,
    holds: HoldsState,
    page: Page
  ): boolean {
    return (
      (currentUser || currentStaffUser) &&
      !checkouts.loadingState.loading &&
      !holds.loadingState.loading &&
      !!page
    );
  }

  private updateUserAndStaffInfo(currentUser: User, currentStaffUser: StaffUser): void {
    this.user = currentUser;
    this.staffUser = currentStaffUser;
  }

  private updateCheckoutAndHoldCounts(checkouts: CheckoutsState, holds: HoldsState): void {
    this.checkoutsCount = checkouts.cards?.length;
    this.holdsCount = holds.cards?.length;
  }

  private updateShowcaseSettings(page: Page): void {
    this.portalFeaturedShowcaseRef = page?.settings?.portal_featured_showcase_ref;
    this.portalFallbackShowcaseRef = page?.settings?.portal_fallback_showcase_ref;
    this.portalFeaturedShowcaseTitle = page?.settings?.portal_featured_showcase_title;
  }

  private updateLists(lists: ListWithItemsCount[]): void {
    const forLaterList = lists.find((list) => list.type === ListType.forLater);
    if (forLaterList) {
      this.forLaterList = this.converToListDto(forLaterList);
    }
    this.bookmarksList = this.listSearchService.generateSearchPreview(lists.flatMap(l => l.items));
  }

  public converToListDto(list: ListWithItemsCount): ForLaterListDto {
    return {
      id: list.id,
      items: {
        data: list.items.map((i) => {
          return {
            ...i.entity,
            coverUrl: i.entity.coverConfig?.coverUrl,
          };
        }),
        page: list.pagination.page,
        totalPages: list.pagination.totalPages,
        totalResults: list.pagination.totalResults,
        sortedBy: `${list.sort.field}.${list.sort.order}`,
      },
      type: ListType.forLater,
    };
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.user = null;
  }

  @HostListener('window:returnToCatalog', ['$event'])
  public onReturnClicked(event: CustomEvent) {
    this.router.navigate(['/']);
  }

  @HostListener('window:itemClicked', ['$event'])
  public onItemClicked(event: CustomEvent) {
    const {id, entityType, rapido = false}: ItemDetail = event.detail;
    this.router.navigate(['/search', 'card'], {
      queryParams: {
        id,
        entityType,
        ...(rapido && {rapido: true})
      }
    });
  }

  @HostListener('window:shareOnSocialMedia', ['$event'])
  public onShareToSocialMediaClick(event: CustomEvent) {
    const {shareData, type}: any = event.detail;
    this.discoverV2BffService.sharePage(shareData).subscribe(
      (data: any) => {
        if (type === SocialMediaType.FACEBOOK) {
          this.createFacebookShareUrl(data.file.location);
        } else {
          this.createTwitterShareUrl(data.file.location);
        }
      });
  }

  @HostListener('window:exportCitation', ['$event'])
  public onExportCitation(event: CustomEvent) {
    const { editionsData, ariaDescribedBy }: any = event.detail || {};
    const modalRef = this.modal.open(CitationExportContentComponent, {
      windowClass: 'inspire-custom-modal',
      ariaDescribedBy,
    });
    modalRef.componentInstance.editionsData = editionsData;
  }

  @HostListener('window:shareViaEmail', ['$event'])
  public onShareViaEmail(event: CustomEvent) {
    const { resourceData, ariaDescribedBy }: any = event.detail || {};
    const modalRef = this.modal.open(EmailPopupComponent, {
      windowClass: 'inspire-custom-modal',
      ariaDescribedBy,
    });
    modalRef.componentInstance.resourceData = resourceData;
  }

  private createTwitterShareUrl(url: string) {
    const shareTwitterUrl = this.shareOnSocialMediaService.socialMediaShareLinkHandler(SocialMediaType.TWITTER, url);
    this.windowRefService.nativeWindow().open(shareTwitterUrl, '_blank');
  }

  private createFacebookShareUrl(url: string) {
    const shareFaceBookUrl = this.shareOnSocialMediaService.socialMediaShareLinkHandler(SocialMediaType.FACEBOOK, url);
    this.windowRefService.nativeWindow().open(shareFaceBookUrl, '_blank');
  }
}
